'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { parseWorkspacePath, type WorkspacePathInfo } from './parseWorkspacePath';

/**
 * Single shared source of truth for the current workspace path
 * (spec: workspace-static-spa-routing).
 *
 * Under static export, `useParams()` / `usePathname()` return the baked
 * placeholder route — useless for a URL-driven shell. This hook reads the live
 * `window.location.pathname` and stays current across History-API navigation
 * (`pushState`/`replaceState`) and browser back/forward (`popstate`). The
 * shell, breadcrumb, and active-nav highlighting all consume this so they never
 * drift from the address bar.
 *
 * Implemented with `useSyncExternalStore` to avoid tearing and to be SSR/export
 * safe (the export prerender has no `window`).
 */

const LOCATION_EVENT = 'sm:locationchange';
let historyPatched = false;

/**
 * Patch `history.pushState`/`replaceState` once so client navigations emit a
 * `locationchange` event (the browser fires `popstate` only for back/forward).
 * Idempotent and StrictMode-safe.
 */
function ensureHistoryPatched(): void {
  if (historyPatched || typeof window === 'undefined') return;
  historyPatched = true;

  // Defer the notification to a microtask. Next's App Router calls
  // `history.replaceState` during React's commit / insertion-effect phase;
  // notifying `useSyncExternalStore` subscribers synchronously there throws
  // "useInsertionEffect must not schedule updates". A microtask runs after the
  // synchronous phase, so the store update is scheduled safely. The URL is
  // already updated synchronously — only the re-render notification is deferred,
  // and an unchanged pathname is a no-op (Object.is in useSyncExternalStore).
  const notify = () => {
    queueMicrotask(() => window.dispatchEvent(new Event(LOCATION_EVENT)));
  };

  const patch = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method];
    window.history[method] = function patched(
      this: History,
      ...args: Parameters<History['pushState']>
    ) {
      const result = original.apply(this, args);
      notify();
      return result;
    };
  };

  patch('pushState');
  patch('replaceState');
  window.addEventListener('popstate', notify);
}

function subscribe(callback: () => void): () => void {
  ensureHistoryPatched();
  window.addEventListener(LOCATION_EVENT, callback);
  return () => window.removeEventListener(LOCATION_EVENT, callback);
}

/**
 * Live pathname, normalized to drop a trailing slash (except root) so it matches
 * `usePathname()` semantics — a drop-in replacement that, unlike `usePathname()`,
 * reflects the real URL under static export. Subscribed to client navigation +
 * popstate.
 */
function getLivePathname(): string {
  const p = window.location.pathname;
  return p.length > 1 && p.endsWith('/') ? p.replace(/\/+$/, '') : p;
}

export function useLocationPathname(): string {
  return useSyncExternalStore(
    subscribe,
    getLivePathname,
    () => '/', // server/export snapshot — gated behind isClient by consumers
  );
}

/**
 * Live `URLSearchParams` from `window.location.search`, subscribed to client
 * navigation + popstate. Use instead of `next/navigation`'s `useSearchParams`
 * inside the workspace shell, where History-API writes don't notify Next's
 * router. Spec: workspace-static-spa-routing.
 */
export function useLocationSearchParams(): URLSearchParams {
  const search = useSyncExternalStore(
    subscribe,
    () => window.location.search,
    () => '',
  );
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Parsed workspace path info for the current URL, recomputed on every client
 * navigation. `base` is the workspace base segment (default `/workspace`).
 */
export function useWorkspacePath(base = '/workspace'): WorkspacePathInfo {
  const pathname = useLocationPathname();
  // base is a stable literal in practice; memoize on both for safety.
  const parse = useCallback((p: string) => parseWorkspacePath(p, base), [base]);
  return useMemo(() => parse(pathname), [parse, pathname]);
}
